import React from 'react';
import { LogOut, Menu, Palette, Monitor, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../hooks/useAppContext';
import { ThemeToggle } from '../ui/ThemeToggle';
import { LanguageToggle } from '../ui/LanguageToggle';
import { Breadcrumbs } from '../common/Breadcrumbs';
import toast from 'react-hot-toast';
import { NotificationBell } from './../common/NotificationBell';

interface HeaderProps {
    toggleMobileMenu: () => void;
}

export const ModernHeader: React.FC<HeaderProps> = ({ toggleMobileMenu }) => {
    const { user, t, lang, logout, toggleDesignMode, designMode } = useAppContext();
    const navigate = useNavigate();
    const location = useLocation();
    const isSubpage = location.pathname.split('/').filter(Boolean).length >= 2;

    const handleSignOut = () => {
        logout();
        toast.success('تم تسجيل الخروج بنجاح.');
    };

    if (!user) return null;

    return (
        <>
            {/* Desktop Header */}
            <header
                className="hidden lg:flex glass-panel mx-4 mt-4 rounded-2xl px-6 py-3 items-center justify-between sticky top-4 z-30 gap-6 transition-all duration-300"
                dir="ltr"
            >
                {/* Left Side: User & Controls */}
                <div className="flex items-center gap-4 flex-shrink-0">
                    {/* User Profile Pill */}
                    <div className="flex items-center gap-3 pl-1 pr-4 py-1 bg-slate-800/50 rounded-full border border-slate-700/50">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-lg shadow-blue-500/20">
                            {user.name.charAt(0)}
                        </div>
                        <div dir={lang === 'ar' ? 'rtl' : 'ltr'} className="flex flex-col">
                            <span className="text-xs font-bold text-slate-200 leading-tight">{user.name}</span>
                            <span className="text-[10px] text-slate-400 leading-tight">{user.position}</span>
                        </div>
                    </div>

                    <div className="h-6 w-px bg-slate-700/50"></div>

                    {/* Action Toggles */}
                    <div className="flex items-center gap-2">
                        <div className="bg-slate-800/50 p-1.5 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors">
                            <NotificationBell />
                        </div>
                        <div className="bg-slate-800/50 p-1.5 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors">
                            <LanguageToggle />
                        </div>
                        <div className="bg-slate-800/50 p-1.5 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors">
                            <ThemeToggle />
                        </div>

                        {isSubpage && (
                            <button
                                onClick={() => navigate(-1)}
                                className="bg-slate-800/50 p-1.5 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors text-slate-400"
                                title="رجوع"
                            >
                                <ArrowRight size={20} />
                            </button>
                        )}

                        {/* Design Mode Toggle */}
                        <button
                            onClick={toggleDesignMode}
                            className="bg-slate-800/50 p-1.5 rounded-xl border border-slate-700/50 hover:border-blue-500/50 hover:text-blue-400 transition-all text-slate-400"
                            title="Switch Design Mode"
                        >
                            {designMode === 'modern' ? <Palette size={20} /> : <Monitor size={20} />}
                        </button>
                    </div>
                </div>

                {/* Right Side: Breadcrumbs */}
                <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                    <Breadcrumbs />
                </div>
            </header>

            {/* Mobile Header */}
            <header
                className="lg:hidden sticky top-0 z-50 p-4 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800"
                dir="ltr"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={toggleMobileMenu}
                            className="p-2.5 rounded-xl bg-slate-800 text-slate-200 border border-slate-700 active:scale-95 transition-transform"
                        >
                            <Menu size={20} />
                        </button>
                        <NotificationBell />
                    </div>

                    <div className="flex items-center gap-2">
                        {isSubpage && (
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 rounded-lg bg-slate-800/50 text-slate-400 border border-slate-700/50"
                            >
                                <ArrowRight size={18} />
                            </button>
                        )}
                        <button
                            onClick={toggleDesignMode}
                            className="p-2 rounded-lg bg-slate-800/50 text-slate-400 border border-slate-700/50"
                        >
                            <Palette size={18} />
                        </button>
                        <ThemeToggle />
                    </div>
                </div>
            </header>
        </>
    );
};
